import { Controller } from '@hotwired/stimulus'

// Fix for Leaflet marker icons when using CDN
if (typeof L !== 'undefined') {
  delete L.Icon.Default.prototype._getIconUrl
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  })
}

export default class extends Controller {
  static targets = [
    'container',
    'latitudeInput',
    'longitudeInput'
  ]

  static values = {
    initialLat: Number,
    initialLng: Number
  }

  connect() {
    // Wait for Leaflet to be loaded from CDN
    this.ensureLeafletLoaded()
  }

  ensureLeafletLoaded() {
    if (typeof L !== 'undefined') {
      this.initMap()
    } else {
      // Retry if Leaflet not loaded yet
      setTimeout(() => this.ensureLeafletLoaded(), 100)
    }
  }

  initMap() {
    const container = this.containerTarget

    // Determine which address prefix to use (main_address or delivery_addresses)
    this.addressPrefix = this.findAddressPrefix()

    // Get coordinates from hidden inputs if available
    let lat = this.initialLatValue || 59.9139
    let lng = this.initialLngValue || 10.7522

    // Check if we have saved coordinates in the hidden inputs
    if (this.hasLatitudeInputTarget && this.hasLongitudeInputTarget) {
      const savedLat = parseFloat(this.latitudeInputTarget.value)
      const savedLng = parseFloat(this.longitudeInputTarget.value)

      if (savedLat && savedLng && !isNaN(savedLat) && !isNaN(savedLng)) {
        lat = savedLat
        lng = savedLng
      }
    }

    // Create map
    this.map = L.map(container).setView([lat, lng], 13)

    // Add OpenStreetMap tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(this.map)

    // Create draggable marker
    this.marker = L.marker([lat, lng], { draggable: true }).addTo(this.map)

    // Update coordinates when marker is dragged
    this.marker.on('dragend', () => {
      const { lat, lng } = this.marker.getLatLng()
      this.updateCoordinates(lat, lng)
    })

    // Handle map click to place marker
    this.map.on('click', (e) => {
      this.marker.setLatLng(e.latlng)
      this.updateCoordinates(e.latlng.lat, e.latlng.lng)
    })

    // Setup event listeners on address inputs for geocoding
    this.setupAddressChangeListeners()

    // Invalidate size when container becomes visible (was hidden via d-none)
    this.resizeObserver = new ResizeObserver(() => {
      if (this.map) this.map.invalidateSize()
    })
    this.resizeObserver.observe(this.containerTarget)
  }

  updateCoordinates(lat, lng) {
    const latitude = parseFloat(lat).toFixed(6)
    const longitude = parseFloat(lng).toFixed(6)

    if (this.hasLatitudeInputTarget) {
      this.latitudeInputTarget.value = latitude
    }
    if (this.hasLongitudeInputTarget) {
      this.longitudeInputTarget.value = longitude
    }
  }

  findAddressPrefix() {
    // Determine if this map is for main_address or delivery_addresses
    // Look in the element tree for the latitude input
    const latInput = this.element.querySelector('input[data-map-target="latitudeInput"]')
    if (!latInput) {
      console.warn('Could not find latitude input for address detection')
      return 'main_address' // default
    }
    
    if (latInput.name.includes('main_address')) {
      return 'main_address'
    } else if (latInput.name.includes('delivery_addresses')) {
      return 'delivery_addresses'
    }
    return 'main_address' // default
  }

  getAddressInputForPrefix(fieldName) {
    // Find input by name pattern based on the address prefix
    if (this.addressPrefix === 'main_address') {
      return document.querySelector(`input[name="client_registration[main_address][${fieldName}]"]`)
    } else {
      return document.querySelector(`input[name="client_registration[delivery_addresses][][${fieldName}]"]`)
    }
  }

  geocodeAddress() {
    let street = this.getAddressInputForPrefix('street')?.value || ''
    const houseNumber = this.getAddressInputForPrefix('house_number')?.value || ''
    const postalCode = this.getAddressInputForPrefix('postal_code')?.value || ''
    const city = this.getAddressInputForPrefix('city')?.value || ''
    const country = this.getAddressInputForPrefix('country')?.value || ''

    if (!street || !city) return

    // Clean street name - remove common prefixes
    street = this.cleanStreetName(street)

    const address = `${street} ${houseNumber}, ${postalCode} ${city}, ${country}`
    const encodedAddress = encodeURIComponent(address)
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}`

    fetch(url)
      .then(response => response.json())
      .then(data => {
        if (data && data.length > 0) {
          const result = data[0]
          const lat = parseFloat(result.lat)
          const lng = parseFloat(result.lon)

          this.map.setView([lat, lng], 13)
          this.marker.setLatLng([lat, lng])
          this.updateCoordinates(lat, lng)
        }
      })
      .catch(error => console.error('Geocoding error:', error))
  }

  cleanStreetName(street) {
    // Remove common street prefixes in different languages
    const prefixes = [
      'ul.', 'ul ', 'ulica ',
      'str.', 'str ', 'street ',
      'ave.', 'ave ', 'avenue ',
      'gt.', 'gt ', 'gate ',
      'vei ', 'veien '
    ]

    let cleaned = street.trim()
    for (const prefix of prefixes) {
      if (cleaned.toLowerCase().startsWith(prefix)) {
        cleaned = cleaned.substring(prefix.length).trim()
        break
      }
    }

    return cleaned
  }

  onAddressChange() {
    clearTimeout(this.geocodingTimeout)
    this.geocodingTimeout = setTimeout(() => {
      this.geocodeAddress()
    }, 1000)
  }

  setupAddressChangeListeners() {
    // Find all address input fields and attach change listeners
    const fieldNames = ['street', 'house_number', 'postal_code', 'city', 'country']
    
    fieldNames.forEach(fieldName => {
      const input = this.getAddressInputForPrefix(fieldName)
      if (input) {
        input.addEventListener('blur', () => this.onAddressChange())
        input.addEventListener('change', () => this.onAddressChange())
      }
    })
  }

  disconnect() {
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.map) this.map.remove()
  }
}
